import { createSlice } from '@reduxjs/toolkit';
import { ReferentialDto, ReferentialKey } from 'api/referential';
import { DropdownItem } from 'components/forms/Dropdown';
import { mapReferentialsToDropdownItems } from '../mappers/referentialMapper';
import { getAllReferentials } from './getAllReferentialsThunk';
import { getMultipleReferentialsByKeys } from './getMultipleReferentialsByKeysThunk';
import { getReferentialByKey } from './getReferentialByKeyThunk';
import { INCOME_TYPES } from './mocks';

export type ReferentialState = {
  referentials: Record<ReferentialKey, DropdownItem[]>;
  referentialsData: Record<ReferentialKey, ReferentialDto[]>;
  loading: boolean;
};

const initialState: ReferentialState = {
  referentials: {
    [ReferentialKey.Civility]: [],
    [ReferentialKey.LegalForm]: [],
    [ReferentialKey.Nationality]: [],
    [ReferentialKey.Country]: [],
    [ReferentialKey.CompanyRole]: [],
    [ReferentialKey.MandateFrequency]: [],
    [ReferentialKey.QuestionsContexts]: [],
    [ReferentialKey.ProjectType]: [],
    [ReferentialKey.InsuranceProject]: [],
    [ReferentialKey.LoanType]: [],
    [ReferentialKey.DefermentType]: [],
    [ReferentialKey.InterestRateType]: [],
    [ReferentialKey.ProspectType]: [],
    [ReferentialKey.EmploymentStatus]: [],
    [ReferentialKey.IncomeType]: [],
    [ReferentialKey.Plateforme]: [],
    [ReferentialKey.Role]: [],
    [ReferentialKey.IntermediateCategory]: [],
    [ReferentialKey.Branch]: [],
    [ReferentialKey.NotePriority]: [],
    [ReferentialKey.NoteStatus]: [],
    [ReferentialKey.NoteTag]: [],
    [ReferentialKey.ContractPeriodicity]: [],
    [ReferentialKey.NoticeUnit]: [],
  },
  referentialsData: {
    [ReferentialKey.Civility]: [],
    [ReferentialKey.LegalForm]: [],
    [ReferentialKey.Nationality]: [],
    [ReferentialKey.Country]: [],
    [ReferentialKey.CompanyRole]: [],
    [ReferentialKey.MandateFrequency]: [],
    [ReferentialKey.QuestionsContexts]: [],
    [ReferentialKey.ProjectType]: [],
    [ReferentialKey.InsuranceProject]: [],
    [ReferentialKey.LoanType]: [],
    [ReferentialKey.DefermentType]: [],
    [ReferentialKey.InterestRateType]: [],
    [ReferentialKey.ProspectType]: [],
    [ReferentialKey.EmploymentStatus]: [],
    [ReferentialKey.IncomeType]: [],
    [ReferentialKey.Plateforme]: [],
    [ReferentialKey.Role]: [],
    [ReferentialKey.IntermediateCategory]: [],
    [ReferentialKey.Branch]: [],
    [ReferentialKey.NotePriority]: [],
    [ReferentialKey.NoteStatus]: [],
    [ReferentialKey.NoteTag]: [],
    [ReferentialKey.ContractPeriodicity]: [],
    [ReferentialKey.NoticeUnit]: [],
  },
  loading: false
};

const referentialSlice = createSlice({
  name: 'referential',
  initialState,
  reducers: {
  },
  extraReducers: builder => {
    builder
      // getAllReferentials
      .addCase(getAllReferentials.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllReferentials.fulfilled, (state, action) => {
        state.loading = false;
        const globalReferentials = action.payload
        state.referentialsData = {
          ...state.referentialsData,
          [ReferentialKey.CompanyRole]: globalReferentials.companyRoles,
          [ReferentialKey.Nationality]: globalReferentials.nationalities,
          [ReferentialKey.LegalForm]: globalReferentials.legalForms,
          [ReferentialKey.MandateFrequency]: globalReferentials.mandateFrequencies,
          [ReferentialKey.Country]: globalReferentials.countries,
          [ReferentialKey.Civility]: globalReferentials.civilities,
          [ReferentialKey.QuestionsContexts]: globalReferentials.questionsContexts,
          [ReferentialKey.IncomeType]: INCOME_TYPES,
        }
        state.referentials = {
          ...state.referentials,
          [ReferentialKey.CompanyRole]: mapReferentialsToDropdownItems(globalReferentials.companyRoles),
          [ReferentialKey.Nationality]: mapReferentialsToDropdownItems(globalReferentials.nationalities),
          [ReferentialKey.LegalForm]: mapReferentialsToDropdownItems(globalReferentials.legalForms),
          [ReferentialKey.MandateFrequency]: mapReferentialsToDropdownItems(globalReferentials.mandateFrequencies),
          [ReferentialKey.Country]: mapReferentialsToDropdownItems(globalReferentials.countries),
          [ReferentialKey.Civility]: mapReferentialsToDropdownItems(globalReferentials.civilities),
          [ReferentialKey.QuestionsContexts]: mapReferentialsToDropdownItems(globalReferentials.questionsContexts),
        };
      })
      .addCase(getAllReferentials.rejected, (state) => {
        state.loading = false;
      })
      // getReferentialByKey
      .addCase(getReferentialByKey.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReferentialByKey.fulfilled, (state, action) => {
        state.loading = false;
        const referential = action.payload.referential
        if (referential) {
          state.referentials = {
            ...state.referentials,
            [action.payload.referentialKey]: mapReferentialsToDropdownItems(referential),
          }
          state.referentialsData = {
            ...state.referentialsData,
            [action.payload.referentialKey]: referential,
          }
        }
      })
      .addCase(getReferentialByKey.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getMultipleReferentialsByKeys.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMultipleReferentialsByKeys.fulfilled, (state, action) => {
        state.loading = false;
        const referentials = action.payload.referentials;

        referentials.forEach(({ referentialKey, referential }) => {
          state.referentialsData = {
            ...state.referentialsData,
            [referentialKey]: Array.isArray(referential) ? referential : [],
          };
          state.referentials = {
            ...state.referentials,
            [referentialKey]: mapReferentialsToDropdownItems(
              Array.isArray(referential) ? referential : []
            ),
          };
        });
      })
      .addCase(getMultipleReferentialsByKeys.rejected, (state) => {
        state.loading = false;
      })
  }
});

export default referentialSlice.reducer;
